/**
 * HelpHint — small "?" icon that opens a rich HoverCard on hover/focus/click.
 *
 * Designed to sit in the corner of a card or beside a heading, providing
 * extra context without cluttering the primary label.
 */
import React from 'react';
import { HelpCircle } from 'lucide-react';
import { HoverCard, HoverCardContent, HoverCardTrigger } from './ui/hover-card';

const HelpHint = ({
  title,
  children,
  bullets,
  cta,
  onCtaClick,
  side = 'right',
  align = 'start',
  size = 'sm',
  className = '',
  testId,
}) => {
  const iconSize = size === 'lg' ? 'w-5 h-5' : 'w-4 h-4';
  return (
    <HoverCard openDelay={80} closeDelay={100}>
      <HoverCardTrigger asChild>
        <span
          role="button"
          tabIndex={0}
          onClick={(e) => { e.stopPropagation(); e.preventDefault(); }}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); e.preventDefault(); } }}
          className={`inline-flex items-center justify-center rounded-full text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors cursor-help ${size === 'lg' ? 'h-7 w-7' : 'h-6 w-6'} ${className}`}
          aria-label={`More info about ${title || 'this'}`}
          data-testid={testId || 'help-hint'}
        >
          <HelpCircle className={iconSize} />
        </span>
      </HoverCardTrigger>
      <HoverCardContent
        side={side}
        align={align}
        sideOffset={8}
        className="w-80 p-0 overflow-hidden"
        data-testid={testId ? `${testId}-content` : 'help-hint-content'}
      >
        {title && (
          <div className="px-4 pt-3.5 pb-1">
            <div className="font-heading text-sm font-extrabold tracking-tight text-foreground">{title}</div>
          </div>
        )}
        {children && (
          <div className="px-4 pb-3 text-xs text-muted-foreground leading-relaxed">{children}</div>
        )}
        {Array.isArray(bullets) && bullets.length > 0 && (
          <ul className="px-4 pb-3 space-y-1.5">
            {bullets.map((b, i) => (
              <li key={i} className="text-xs text-muted-foreground flex items-start gap-2">
                <span className="mt-1 inline-block h-1 w-1 rounded-full bg-primary flex-shrink-0" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        )}
        {cta && onCtaClick && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onCtaClick(e); }}
            className="w-full px-4 py-2.5 text-xs font-semibold text-primary hover:bg-primary/10 border-t border-border text-left transition-colors"
          >
            {cta} →
          </button>
        )}
      </HoverCardContent>
    </HoverCard>
  );
};

export default HelpHint;
